import { useCallback, useEffect, useState } from "react";
import { useApi } from "./useApi";
import { loadFallbackSchemaSdl, isValidGraphqlSdl, getBundledSchemaSdl } from "../utils/graphqlSchemaParser";

const GET_API_SCHEMA = `
  query GetApiSchema {
    getApiSchema {
      version
      sdl
      operations {
        name
        kind
        domain
        description
        min_role
        parameters {
          name
          type
          required
          description
        }
      }
    }
  }
`;

const OPERATIONS_ONLY = `
  query GetApiSchema {
    getApiSchema {
      version
      operations {
        name
        kind
        domain
        description
        min_role
        parameters {
          name
          type
          required
          description
        }
      }
    }
  }
`;

async function resolveSdl(apiSdl) {
  if (isValidGraphqlSdl(apiSdl)) {
    return { sdl: apiSdl, source: "api" };
  }
  try {
    const local = await loadFallbackSchemaSdl();
    return { sdl: local, source: "bundled" };
  } catch (err) {
    const bundled = getBundledSchemaSdl();
    if (isValidGraphqlSdl(bundled)) {
      return { sdl: bundled, source: "bundled" };
    }
    return { sdl: "", source: "none", fallbackError: err.message };
  }
}

export function useGraphqlSchema() {
  const { callApi } = useApi();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sdlSource, setSdlSource] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let apiData = null;
      try {
        apiData = await callApi(GET_API_SCHEMA);
      } catch (apiErr) {
        try {
          apiData = await callApi(OPERATIONS_ONLY);
        } catch {
          throw apiErr;
        }
      }

      const payload = apiData?.getApiSchema;
      if (!payload) {
        throw new Error("getApiSchema no devolvió datos");
      }

      const { sdl, source, fallbackError } = await resolveSdl(payload.sdl);
      setSdlSource(source);
      setSchema({ ...payload, sdl });

      if (fallbackError && source === "none") {
        setError(fallbackError);
      }
    } catch (err) {
      const bundled = getBundledSchemaSdl();
      if (isValidGraphqlSdl(bundled)) {
        setSchema({ version: "local", sdl: bundled, operations: [] });
        setSdlSource("bundled");
        setError(null);
      } else {
        setError(err.message || "Error loading schema");
        setSchema(null);
      }
    } finally {
      setLoading(false);
    }
  }, [callApi]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { schema, loading, error, refresh, sdlSource };
}
